/**
 * Query Keys Factory
 * Centralized query key management for TanStack Query
 */

import type { IOrganizationListParams } from '@/entities/organization/model'
import type { ISpecializedDonorListParams } from '@/entities/specialized-donor/model'
import type {
  IDashboardFilters,
  IRecentDonorsParams,
  IStockGroupParams,
} from '@/entities/dashboard/model'
import type {
  IDeferralListParams,
  IDeferralSummaryParams,
} from '@/entities/deferral/model'
import type { IScreeningQueueParams } from '@/entities/screening/model'
import type {
  IBloodCollectionParams,
  IBloodCollectionQueueParams,
} from '@/entities/blood-collection/model'
import type { IAnalysisDefinitionParams } from '@/entities/analysis-definition'
import type { IOrganizationAnalysisConfigParams } from '@/entities/organization-analysis-config'
import type { ILabOrdersParams, ILabPcrParams } from '@/entities/lab/model'
import type { IDonationCandidateParams } from '@/entities/donation/model'
import type { IContainerParams } from '@/entities/container/model'
import type {
  IContractParams,
  IContractPayload,
} from '@/entities/contract/model'
import type { IProcessingDonationParams } from '@/entities/processing/model'
import type { IRecipientListParams } from '@/entities/recipient/model'
import type { ITransfusionListParams } from '@/entities/transfusion/model'
import type { IBloodConsumptionListParams } from '@/entities/blood-consumption/model'
import type { IBloodApplicationListParams } from '@/entities/blood-application/model'
import type { IEmergencyNotificationJournalParams } from '@/entities/emergency-notification/model'
import type {
  ICryoprecipitateRecordParams,
  IJournalComponentParams,
  ISecondaryProcessingParams,
  TJournalModule,
} from '@/entities/component-journal/model'
import type {
  IIssuanceApplicationParams,
  IIssuanceSummaryParams,
  IReceiptParams,
  IWarehouseParams,
} from '@/entities/issuance/model'

export const queryKeys = {
  // Base keys
  all: ['api'] as const,

  // Questionnaire queries

  questionnaire: {
    all: (params?: { gender?: string; donorId?: string }) =>
      [...queryKeys.all, 'questionnaire', params ?? {}] as const,
    byVisit: (visitId: string) =>
      [...queryKeys.all, 'questionnaire', 'byVisit', visitId] as const,
    surveyHistory: (donorId: string) =>
      [...queryKeys.all, 'questionnaire', 'surveyHistory', donorId] as const,
    surveyHistoryDetail: (historyId: string) =>
      [
        ...queryKeys.all,
        'questionnaire',
        'surveyHistoryDetail',
        historyId,
      ] as const,
  },

  // Visit queries

  // Дашборд Службы крови (BS-229) — names are localized, hence `lang`.
  dashboard: {
    all: ['api', 'dashboard'] as const,
    summary: (filters: IDashboardFilters, lang: string) =>
      [...queryKeys.dashboard.all, 'summary', filters, lang] as const,
    stockGroup: (params: IStockGroupParams | null, lang: string) =>
      [...queryKeys.dashboard.all, 'stock-group', params, lang] as const,
    recentDonors: (params: IRecentDonorsParams, lang: string) =>
      [...queryKeys.dashboard.all, 'recent-donors', params, lang] as const,
  },

  // Реестр специализированных доноров (BS-236).
  specializedDonors: {
    all: ['api', 'specialized-donors'] as const,
    list: (params: ISpecializedDonorListParams) =>
      [...queryKeys.specializedDonors.all, 'list', params] as const,
  },

  // Журнал отводов (BS-237) — the «Отводы» tab and the donor's history.
  deferrals: {
    all: ['api', 'deferrals'] as const,
    list: (params: IDeferralListParams) =>
      [...queryKeys.deferrals.all, 'list', params] as const,
    summary: (params: IDeferralSummaryParams) =>
      [...queryKeys.deferrals.all, 'summary', params] as const,
    donor: (donorId: string) =>
      [...queryKeys.deferrals.all, 'donor', donorId] as const,
  },

  visits: {
    admitted: () => [...queryKeys.all, 'visits', 'admitted'] as const,
    /** Endless scroll over the admitted queue — a different cache entry. */
    admittedInfinite: (size: number, search?: string) =>
      [
        ...queryKeys.all,
        'visits',
        'admitted',
        'infinite',
        size,
        search ?? '',
      ] as const,
    examinations: (visitId: string) =>
      [...queryKeys.all, 'visits', 'examinations', visitId] as const,
    donationEligibility: (pinfl: string, donationTypeId: string) =>
      [
        ...queryKeys.all,
        'visits',
        'donation-eligibility',
        pinfl,
        donationTypeId,
      ] as const,
  },

  // Screening queries
  screening: {
    all: () => [...queryKeys.all, 'screening'] as const,
    queue: (params: IScreeningQueueParams) =>
      [...queryKeys.all, 'screening', 'queue', params] as const,
    history: (visitId: string) =>
      [...queryKeys.all, 'screening', 'history', visitId] as const,
  },

  // Blood collection queries
  bloodCollection: {
    all: () => [...queryKeys.all, 'bloodCollection'] as const,
    queue: (params: IBloodCollectionQueueParams) =>
      [...queryKeys.all, 'bloodCollection', 'queue', params] as const,
    list: (params: IBloodCollectionParams) =>
      [...queryKeys.all, 'bloodCollection', 'list', params] as const,
    detail: (collectionStageId: string) =>
      [
        ...queryKeys.all,
        'bloodCollection',
        'detail',
        collectionStageId,
      ] as const,
  },

  // Donations queries
  donations: {
    all: () => [...queryKeys.all, 'donations'] as const,
    candidates: (params: IDonationCandidateParams) =>
      [...queryKeys.all, 'donations', 'candidates', params] as const,
    /** Option lists behind the donation form. */
    context: () => [...queryKeys.all, 'donations', 'context'] as const,
    /** Recorded donations fetched by uuid — the label data. */
    byUuids: (uuids: string[]) =>
      [...queryKeys.all, 'donations', 'byUuids', uuids] as const,
  },

  // Warehouse container queries
  containers: {
    all: () => [...queryKeys.all, 'containers'] as const,
    list: (params: IContainerParams) =>
      [...queryKeys.all, 'containers', 'list', params] as const,
    detail: (id: number) =>
      [...queryKeys.all, 'containers', 'detail', id] as const,
  },

  // Lab orders queries
  lab: {
    all: () => [...queryKeys.all, 'lab'] as const,
    orders: (params: ILabOrdersParams) =>
      [...queryKeys.all, 'lab', 'orders', params] as const,
    byBarcode: (barcode: string) =>
      [...queryKeys.all, 'lab', 'orders', 'byBarcode', barcode] as const,
    detail: (orderId: string) =>
      [...queryKeys.all, 'lab', 'orders', 'detail', orderId] as const,
    analysesStats: () =>
      [...queryKeys.all, 'lab', 'analyses', 'stats'] as const,
    analysisResultForm: (analysisId: string) =>
      [...queryKeys.all, 'lab', 'analyses', analysisId, 'resultForm'] as const,
    pcr: (params: ILabPcrParams) =>
      [...queryKeys.all, 'lab', 'pcr', params] as const,
  },

  // Analyses queries
  analyses: {
    all: () => [...queryKeys.all, 'analyses'] as const,
    detail: (analysisId: string) =>
      [...queryKeys.all, 'analyses', 'detail', analysisId] as const,
  },

  // User queries
  users: {
    me: () => ['api', 'user', 'me'] as const,
    exists: (inn: string) => ['api', 'user', 'exists', inn] as const,
  },

  // Application queries
  applications: {
    all: ['api', 'applications'] as const,
    listsShort: () => [...queryKeys.applications.all, 'listShort'] as const,
    listShort: (filters?: Record<string, unknown>) =>
      [...queryKeys.applications.listsShort(), filters] as const,
    lists: () => [...queryKeys.applications.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.applications.lists(), filters] as const,
    details: () => [...queryKeys.applications.all, 'detail'] as const,
    detail: (id: string | number) =>
      [...queryKeys.applications.details(), id] as const,
    resultDetail: (id: string) => ['result', id] as const,
  },

  // Dashboard queries
  // Laboratory queries
  labs: {
    all: ['api', 'labs'] as const,
    lists: () => [...queryKeys.labs.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.labs.lists(), filters] as const,
    detail: (id: number) => [...queryKeys.labs.all, 'detail', id] as const,
    photo: (id: number) => [...queryKeys.labs.all, 'photo', id] as const,
  },

  // Document queries
  documents: {
    all: ['api', 'documents'] as const,
    lists: () => [...queryKeys.documents.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.documents.lists(), filters] as const,
    detail: (id: string) => [...queryKeys.documents.all, 'detail', id] as const,
  },

  // Profile queries
  profile: {
    all: ['api', 'profile'] as const,
    detail: () => [...queryKeys.profile.all, 'detail'] as const,
  },
  donors: {
    all: ['api', 'donors'] as const,
    lists: () => [...queryKeys.donors.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.donors.lists(), filters] as const,
    detail: (id: string) => [...queryKeys.donors.all, 'detail', id] as const,
    cardHistory: (donorId: string) =>
      [...queryKeys.donors.all, 'card-history', donorId] as const,
  },
  donorCard: {
    all: ['api', 'donorCard'] as const,
    detail: (id: string) => [...queryKeys.donorCard.all, 'detail', id] as const,
    labStudies: (filters?: Record<string, unknown>) =>
      [...queryKeys.donorCard.all, 'lab-studies', filters] as const,
    documents: (donorId: string) =>
      [...queryKeys.donorCard.all, 'documents', donorId] as const,
  },
  certificate: {
    all: ['api', 'certificate'] as const,
    lists: () => [...queryKeys.certificate.all, 'list'] as const,
    list: (donorId: string) =>
      [...queryKeys.certificate.lists(), donorId] as const,
    details: () => [...queryKeys.certificate.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.certificate.details(), id] as const,
    context: (donorId: string) =>
      [...queryKeys.certificate.all, 'context', donorId] as const,
    verify: (token: string) =>
      [...queryKeys.certificate.all, 'verify', token] as const,
  },
  regions: {
    all: ['api', 'regions'] as const,
    lists: () => [...queryKeys.regions.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.regions.lists(), filters] as const,
  },
  districts: {
    all: ['api', 'districts'] as const,
    byRegion: (regionId: string) =>
      [...queryKeys.districts.all, 'byRegion', regionId] as const,
  },
  researchTypes: {
    all: ['api', 'researchTypes'] as const,
    lists: () => [...queryKeys.researchTypes.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.researchTypes.lists(), filters] as const,
  },
  notifications: {
    all: ['api', 'notifications'] as const,
    list: () => [...queryKeys.notifications.all, 'list'] as const,
  },
  files: {
    all: ['api', 'files'] as const,
    detail: () => [...queryKeys.files.all, 'detail'] as const,
    /** Cached bytes of an inline image preview. */
    preview: (id: string) => [...queryKeys.files.all, 'preview', id] as const,
  },
  organizations: {
    all: ['api', 'organizations'] as const,
    list: (params: IOrganizationListParams) =>
      [...queryKeys.organizations.all, 'list', params] as const,
    detail: (inn: string) =>
      [...queryKeys.organizations.all, 'detail', inn] as const,
    stations: () => [...queryKeys.organizations.all, 'stations'] as const,
  },
  // Platform-wide analysis structures — read-only, shared by all organizations.
  analysisDefinitions: {
    all: ['api', 'analysis-definitions'] as const,
    lists: () => [...queryKeys.analysisDefinitions.all, 'list'] as const,
    list: (params: IAnalysisDefinitionParams) =>
      [...queryKeys.analysisDefinitions.lists(), params] as const,
    details: () => [...queryKeys.analysisDefinitions.all, 'detail'] as const,
    detail: (id: string) =>
      [...queryKeys.analysisDefinitions.details(), id] as const,
  },
  // Supply contracts with partner institutions.
  contracts: {
    all: ['api', 'contracts'] as const,
    lists: () => [...queryKeys.contracts.all, 'list'] as const,
    list: (params: IContractParams) =>
      [...queryKeys.contracts.lists(), params] as const,
    details: () => [...queryKeys.contracts.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.contracts.details(), id] as const,
    /** Rendered document body of a saved contract. */
    html: (id: string) => [...queryKeys.contracts.all, 'html', id] as const,
    /** Document rendered from unsaved form values, keyed by the payload itself. */
    preview: (payload: IContractPayload) =>
      [...queryKeys.contracts.all, 'preview', payload] as const,
  },

  // Переработка — donations awaiting processing and their cards.
  processing: {
    all: ['api', 'processing'] as const,
    donations: (params: IProcessingDonationParams) =>
      [...queryKeys.processing.all, 'donations', params] as const,
    cards: () => [...queryKeys.processing.all, 'card'] as const,
    card: (cardId: string) =>
      [...queryKeys.processing.cards(), cardId] as const,
    /** Print data for one dose — only exists once it has a barcode. */
    label: (componentId: string) =>
      [...queryKeys.processing.all, 'label', componentId] as const,
    /** Scanner lookup: label fields plus the movement trail. */
    byBarcode: (barcode: string) =>
      [...queryKeys.processing.all, 'byBarcode', barcode] as const,
  },

  // Receiving-module journals; `module` is the URL segment.
  componentJournal: {
    all: ['api', 'component-journal'] as const,
    module: (module: TJournalModule) =>
      [...queryKeys.componentJournal.all, module] as const,
    list: (module: TJournalModule, params: IJournalComponentParams) =>
      [...queryKeys.componentJournal.module(module), 'list', params] as const,
  },

  // Вторичная переработка — rounds of extra treatment on expedition doses.
  secondaryProcessing: {
    all: ['api', 'secondary-processing'] as const,
    lists: () => [...queryKeys.secondaryProcessing.all, 'list'] as const,
    list: (params: ISecondaryProcessingParams) =>
      [...queryKeys.secondaryProcessing.lists(), params] as const,
    details: () => [...queryKeys.secondaryProcessing.all, 'detail'] as const,
    /** Keyed by the round id, not by the component. */
    detail: (recordId: string) =>
      [...queryKeys.secondaryProcessing.details(), recordId] as const,
  },

  // Cryoprecipitate preparation journal.
  cryoprecipitateRecords: {
    all: ['api', 'cryoprecipitate-records'] as const,
    lists: () => [...queryKeys.cryoprecipitateRecords.all, 'list'] as const,
    list: (params: ICryoprecipitateRecordParams) =>
      [...queryKeys.cryoprecipitateRecords.lists(), params] as const,
  },

  // Patients institutions request components for.
  recipients: {
    all: ['api', 'recipients'] as const,
    lists: () => [...queryKeys.recipients.all, 'list'] as const,
    list: (params: IRecipientListParams) =>
      [...queryKeys.recipients.lists(), params] as const,
    /** Endless scroll over the same list — a different cache entry. */
    infinite: (params?: Record<string, unknown>) =>
      [...queryKeys.recipients.lists(), 'infinite', params] as const,
    details: () => [...queryKeys.recipients.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.recipients.details(), id] as const,
  },

  // Recorded transfusions of a component dose into a recipient.
  transfusions: {
    all: ['api', 'transfusions'] as const,
    lists: () => [...queryKeys.transfusions.all, 'list'] as const,
    list: (params: ITransfusionListParams) =>
      [...queryKeys.transfusions.lists(), params] as const,
    details: () => [...queryKeys.transfusions.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.transfusions.details(), id] as const,
  },

  // Institutions' requests for component doses.
  bloodApplications: {
    all: ['api', 'blood-applications'] as const,
    lists: () => [...queryKeys.bloodApplications.all, 'list'] as const,
    list: (params: IBloodApplicationListParams) =>
      [...queryKeys.bloodApplications.lists(), params] as const,
    details: () => [...queryKeys.bloodApplications.all, 'detail'] as const,
    detail: (id: string) =>
      [...queryKeys.bloodApplications.details(), id] as const,
  },

  // An organization's own settings on top of a definition.
  organizationAnalysisConfigs: {
    all: ['api', 'organization-analysis-configs'] as const,
    lists: () =>
      [...queryKeys.organizationAnalysisConfigs.all, 'list'] as const,
    list: (params: IOrganizationAnalysisConfigParams) =>
      [...queryKeys.organizationAnalysisConfigs.lists(), params] as const,
    details: () =>
      [...queryKeys.organizationAnalysisConfigs.all, 'detail'] as const,
    detail: (id: string) =>
      [...queryKeys.organizationAnalysisConfigs.details(), id] as const,
  },

  // Выдача компонентов крови — worklist, batches, transfer cards, institution stock.
  issuance: {
    all: ['api', 'issuance'] as const,
    applications: () => [...queryKeys.issuance.all, 'applications'] as const,
    applicationList: (params: IIssuanceApplicationParams) =>
      [...queryKeys.issuance.applications(), 'list', params] as const,
    summary: (params: IIssuanceSummaryParams) =>
      [...queryKeys.issuance.all, 'summary', params] as const,
    applicationDetails: () =>
      [...queryKeys.issuance.all, 'application'] as const,
    /** Keyed by the business number — the path id of the detail endpoint. */
    application: (id: string) =>
      [...queryKeys.issuance.applicationDetails(), id] as const,
    lineUnits: (id: string, lineId: string) =>
      [
        ...queryKeys.issuance.applicationDetails(),
        id,
        'lines',
        lineId,
        'units',
      ] as const,
    history: (id: string) =>
      [...queryKeys.issuance.applicationDetails(), id, 'history'] as const,
    receipts: () => [...queryKeys.issuance.all, 'receipts'] as const,
    receiptList: (params: IReceiptParams) =>
      [...queryKeys.issuance.receipts(), 'list', params] as const,
    receipt: (id: string) =>
      [...queryKeys.issuance.receipts(), 'detail', id] as const,
    receiptCards: (id: string) =>
      [...queryKeys.issuance.receipts(), 'detail', id, 'cards'] as const,
    card: (cardId: string) =>
      [...queryKeys.issuance.all, 'card', cardId] as const,
    warehouse: () => [...queryKeys.issuance.all, 'warehouse'] as const,
    warehouseList: (params: IWarehouseParams) =>
      [...queryKeys.issuance.warehouse(), 'list', params] as const,
  },

  // Протоколы расхода крови (BS-257) — doses a medical institution used up.
  bloodConsumption: {
    all: ['api', 'blood-consumption'] as const,
    availableUnits: () =>
      [...queryKeys.bloodConsumption.all, 'available-units'] as const,
    lists: () => [...queryKeys.bloodConsumption.all, 'list'] as const,
    list: (params: IBloodConsumptionListParams) =>
      [...queryKeys.bloodConsumption.lists(), params] as const,
    details: () => [...queryKeys.bloodConsumption.all, 'detail'] as const,
    detail: (id: string) =>
      [...queryKeys.bloodConsumption.details(), id] as const,
  },

  // Экстренные извещения (форма №129) raised on a positive infection result.
  emergencyNotifications: {
    all: ['api', 'emergency-notifications'] as const,
    journals: () =>
      [...queryKeys.emergencyNotifications.all, 'journal'] as const,
    journal: (params: IEmergencyNotificationJournalParams) =>
      [...queryKeys.emergencyNotifications.journals(), params] as const,
    details: () => [...queryKeys.emergencyNotifications.all, 'detail'] as const,
    detail: (id: string) =>
      [...queryKeys.emergencyNotifications.details(), id] as const,
    /** Rendered HTML of form №129, not the PDF. */
    preview: (id: string) =>
      [...queryKeys.emergencyNotifications.all, 'preview', id] as const,
  },
} as const
